// 고객 정보 인터페이스
export interface Customer {
  user_idx: number;
  user_name: string;
  user_id: string;
  user_email: string;
  marketing_check: boolean;
  user_paid: number;
  total_keywords: number;
  active_keywords: number;
  last_datetime: string;
}

// 고객 목록 API 응답 인터페이스
export interface CustomerListResponse {
  customers: Customer[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// 고객 검색 파라미터 인터페이스
export interface CustomerSearchParams {
  page?: number;
  limit?: number;
  q?: string; // 검색어 (이름, 아이디, 이메일, 숫자만 입력 시 user_idx)
}

/** GET /api/customers/:userIdx/ip-allowlist 한 행 */
export interface IpAllowlistItem {
  allowlist_idx: number;
  user_idx: number;
  cidr: string;
  create_datetime: string | null;
  delete_datetime: string | null;
  is_active: boolean;
  range_summary_ko: string;
}

export interface IpAllowlistListResponse {
  user_idx: number;
  total: number;
  page: number;
  limit: number;
  total_pages: number;
  sort: string;
  status: string;
  items: IpAllowlistItem[];
}

/** PATCH /customers/:userIdx — 이름·사업자/법인 등록번호 갱신 요청 본문 */
export interface CustomerProfileUpdatePayload {
  이름: string;
  사업자등록증: string | null;
  법인등록증: string | null;
}

// 고객 상세 정보 인터페이스
export interface CustomerDetail {
  user_idx: number;
  사용자유형: string;
  이름: string;
  아이디: string;
  이메일: string;
  연락처: string;
  사업자등록증: string | null;
  법인등록증: string | null;
  가입일: string;
  최근로그인: string;
  마케팅동의: boolean;
  잔여포인트: number;
  전일사용포인트: number;
  총결제금액: number;
  한달내결제금액: number;
  활성키워드수: number;
  비활성키워드수: number;
  전일메시지수: number;
}

// 페이지네이션 옵션
export const CUSTOMER_LIMIT_OPTIONS = [15, 30, 50, 100] as const;
export type CustomerLimitOption = typeof CUSTOMER_LIMIT_OPTIONS[number];

// 결제 내역 인터페이스
export interface PaymentHistory {
  결제상품명: string;
  결제금액: number;
  충전포인트: number;
  결제수단: string;
  카드사: string;
  카드번호: string;
  승인일시: string;
}

// 결제 내역 목록 응답 인터페이스
export interface PaymentHistoryListResponse {
  data: PaymentHistory[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// 결제 내역 조회 파라미터 인터페이스
export interface PaymentHistoryParams {
  userIdx: number;
  page?: number;
  limit?: number;
}

// 키워드 설정 인터페이스
export interface KeywordSetting {
  keyword_idx?: number; // 키워드 식별자 (수집 데이터 조회 시 필요)
  생성일자: string;
  타겟사이트: string;
  브랜드명: string;
  키워드: string;
  수집주기: string;
  분석유형: string;
  수집량: number;
  분석량: number;
  활성화: string;
}

// 키워드 설정 목록 응답 인터페이스
export interface KeywordSettingListResponse {
  total: number;
  page: number;
  limit: number;
  items: KeywordSetting[];
}

// 키워드 설정 조회 파라미터 인터페이스
export interface KeywordSettingParams {
  userIdx: number;
  page?: number;
  limit?: number;
}

// 키워드 수집 데이터 아이템 인터페이스 (신규 API)
export interface KeywordDataItem {
  _id: string;
  타겟사이트: string;
  브랜드: string;
  키워드: string;
  제목: string | null;
  작성자: string | null;
  링크: string | null;
  작성시간: string | null;
  분석결과: 'NORMAL' | 'NEGATIVE' | null;
  분석요약: string | null;
  수집일시: string;
  /** Mongo scrap 객체 전체 (본문·추가 필드 등) */
  scrap?: Record<string, unknown> | null;
  /** Mongo analy 객체 전체 */
  analy?: Record<string, unknown> | null;
  ai_type?: string | number | null;
}

// 키워드 수집 데이터 응답 인터페이스
export interface KeywordDataResponse {
  total: number;
  page: number;
  limit: number;
  total_pages: number;
  keyword_info: {
    keyword_idx: number;
    target: string;
    brand: string;
    keyword: string;
  };
  items: KeywordDataItem[];
}

// 포인트 내역 인터페이스
export interface PointHistory {
  생성일자: string;
  설명: string;
  유형: string;
  포인트: number;
  잔여포인트: number;
}

// 포인트 내역 목록 응답 인터페이스
export interface PointHistoryListResponse {
  total: number;
  page: number;
  limit: number;
  total_pages: number;
  items: PointHistory[];
}

// 포인트 내역 조회 파라미터 인터페이스
export interface PointHistoryParams {
  userIdx: number;
  page?: number;
  limit?: number;
} 